import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import { useLocation } from '../../contexts/LocationContext'

// Chart container component
ChartContainer.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

function ChartContainer({ title, description, children, height = 'h-96' }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        )}
      </div>
      <div className={height}>{children}</div>
    </div>
  )
}

// Total Enrollment Trends Chart
function SecondarySchoolEnrollmentTrends({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate data by year and gender
    const aggregatedData = {}

    data.forEach(record => {
      const year = record.year
      const gender = record.gender
      const total = parseFloat(record.total) || 0

      if (!aggregatedData[year]) {
        aggregatedData[year] = { Boys: 0, Girls: 0 }
      }

      aggregatedData[year][gender] = (aggregatedData[year][gender] || 0) + total
    })

    // Convert to array format for ECharts
    const years = Object.keys(aggregatedData).sort()
    const boysData = years.map(year => aggregatedData[year].Boys || 0)
    const girlsData = years.map(year => aggregatedData[year].Girls || 0)
    const totalData = years.map(
      year =>
        (aggregatedData[year].Boys || 0) + (aggregatedData[year].Girls || 0)
    )

    return {
      years,
      series: [
        {
          name: 'Boys',
          data: boysData,
          type: 'line',
          smooth: true,
          itemStyle: {
            color: '#3b82f6',
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
          lineStyle: {
            width: 3,
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
        {
          name: 'Girls',
          data: girlsData,
          type: 'line',
          smooth: true,
          itemStyle: {
            color: '#ef4444',
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
          lineStyle: {
            width: 3,
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
        {
          name: 'Total',
          data: totalData,
          type: 'line',
          smooth: true,
          itemStyle: {
            color: '#10b981',
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
          lineStyle: {
            width: 3,
            type: 'dashed',
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
          },
        },
      ],
    }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      formatter: function (params) {
        let content = `<strong>${params[0].axisValue}</strong><br/>`
        params.forEach(param => {
          content += `${param.seriesName}: ${param.value.toLocaleString()}<br/>`
        })
        return content
      },
    },
    legend: {
      bottom: 10,
      itemGap: 20,
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    grid: {
      left: 60,
      right: 30,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280' },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: {
        color: '#6b7280',
        formatter: '{value}',
      },
      splitLine: {
        show: false,
      },
    },
    series: chartData?.series || [],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No enrollment data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Gender Parity Evolution Chart
function SecondarySchoolGenderParity({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate data by year
    const aggregatedData = {}

    data.forEach(record => {
      const year = record.year
      const gender = record.gender
      const total = parseFloat(record.total) || 0

      if (!aggregatedData[year]) {
        aggregatedData[year] = { Boys: 0, Girls: 0 }
      }

      aggregatedData[year][gender] = (aggregatedData[year][gender] || 0) + total
    })

    // Calculate parity ratios
    const years = Object.keys(aggregatedData).sort()
    const boysData = years.map(year => aggregatedData[year].Boys || 0)
    const girlsData = years.map(year => aggregatedData[year].Girls || 0)
    const parityRatios = years.map(year => {
      const boys = aggregatedData[year].Boys || 0
      const girls = aggregatedData[year].Girls || 0
      return boys > 0 ? (girls / boys) * 100 : 0
    })

    return {
      years,
      boysData,
      girlsData,
      parityRatios,
    }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    legend: {
      bottom: 10,
      itemGap: 20,
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    grid: {
      left: 60,
      right: 60,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      axisLine: {
        lineStyle: { color: '#e5e7eb' },
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      axisLabel: {
        color: '#6b7280',
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Enrollment',
        position: 'left',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        splitLine: { show: false, lineStyle: { color: '#f3f4f6' } },
      },
      {
        type: 'value',
        name: 'Parity Ratio (%)',
        position: 'right',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}%',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'Boys',
        type: 'bar',
        yAxisIndex: 0,
        data: chartData?.boysData || [],
        itemStyle: {
          color: '#3b82f6',
        },
      },
      {
        name: 'Girls',
        type: 'bar',
        yAxisIndex: 0,
        barGap: 0,
        data: chartData?.girlsData || [],
        itemStyle: {
          color: '#ef4444',
        },
      },
      {
        name: 'Parity Ratio',
        type: 'line',
        yAxisIndex: 1,
        data: chartData?.parityRatios || [],
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 3 },
        markLine: {
          silent: true,
          data: [
            {
              yAxis: 100,
              lineStyle: { color: '#f59e0b', type: 'dashed', width: 2 },
              label: {
                formatter: 'Parity (100%)',
                position: 'end',
                fontFamily:
                  'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
              },
            },
          ],
        },
      },
    ],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No parity data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// School Performance Trajectory Chart (Small Multiples)
function SchoolPerformanceTrajectory({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Group data by school and calculate enrollment trends
    const schoolData = {}

    data.forEach(record => {
      const school = record.school_name
      const year = record.year
      const total = parseFloat(record.total) || 0

      if (!schoolData[school]) {
        schoolData[school] = {}
      }
      if (!schoolData[school][year]) {
        schoolData[school][year] = 0
      }
      schoolData[school][year] += total
    })

    // Convert to array format and calculate growth rates
    const schoolTrends = Object.entries(schoolData).map(
      ([school, yearData]) => {
        const years = Object.keys(yearData).sort()
        const enrollments = years.map(year => yearData[year])

        // Calculate growth rate (last year vs first year)
        const firstYear = enrollments[0] || 0
        const lastYear = enrollments[enrollments.length - 1] || 0
        const growthRate =
          firstYear > 0 ? ((lastYear - firstYear) / firstYear) * 100 : 0

        return {
          school,
          years,
          enrollments,
          growthRate,
          totalStudents: lastYear,
        }
      }
    )

    // Sort by growth rate (descending)
    schoolTrends.sort((a, b) => b.growthRate - a.growthRate)

    // Take top 12 schools for display
    return schoolTrends.slice(0, 12)
  }, [data])

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No school performance data available</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {chartData.map(school => {
        const option = {
          title: {
            show: false,
            text:
              school.school.length > 25
                ? school.school.substring(0, 25) + '...'
                : school.school,
            textStyle: {
              fontSize: 10,
              fontWeight: 'bold',
              fontFamily:
                'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
            },
            top: 5,
          },
          grid: {
            left: 30,
            right: 10,
            top: 6,
            bottom: 40,
            containLabel: false,
          },
          xAxis: {
            type: 'category',
            data: school.years,
            axisLabel: {
              fontSize: 8,
              fontFamily:
                'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
            },
            axisLine: { show: false },
            axisTick: { show: false },
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              fontSize: 8,
              formatter: '{value}',
              fontFamily:
                'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
            },
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false, lineStyle: { color: '#f0f0f0' } },
          },
          series: [
            {
              data: school.enrollments,
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 3,
              itemStyle: {
                color: school.growthRate > 0 ? '#10b981' : '#ef4444',
              },
              lineStyle: { width: 2 },
            },
          ],
          tooltip: {
            trigger: 'axis',
            formatter: function (params) {
              const point = params[0]
              return `<b>${school.school}</b><br/>${point.name}: ${point.value} students<br/>Growth: ${school.growthRate.toFixed(1)}%`
            },
          },
        }

        return (
          <div
            key={school.school}
            className="border rounded-lg p-3"
            style={{ height: '160px' }}
          >
            <div className="mb-2">
              <h4
                className="text-sm font-medium text-gray-900 truncate"
                title={school.school}
              >
                {school.school}
              </h4>
            </div>
            <ReactECharts
              option={option}
              style={{ height: '100%', width: '100%' }}
              theme="ug-dashboard"
            />
          </div>
        )
      })}
    </div>
  )
}

// Grade-Level Retention Analysis Chart
function GradeLevelRetentionAnalysis({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate data by year and grade level
    const gradeData = {}

    data.forEach(record => {
      const year = record.year
      if (!gradeData[year]) {
        gradeData[year] = { s1: 0, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0 }
      }

      // Sum up enrollment for each grade level
      ;['s1', 's2', 's3', 's4', 's5', 's6'].forEach(grade => {
        const value = parseFloat(record[grade]) || 0
        gradeData[year][grade] += value
      })
    })

    const years = Object.keys(gradeData).sort()
    const grades = ['s1', 's2', 's3', 's4', 's5', 's6']
    const gradeLabels = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6']

    const series = grades.map((grade, index) => ({
      name: gradeLabels[index],
      type: 'line',
      stack: 'Total',
      smooth: true,
      symbol: 'circle',
      areaStyle: {},
      emphasis: { focus: 'series' },
      data: years.map(year => gradeData[year][grade]),
      itemStyle: {
        color: `hsl(${index * 60}, 70%, 50%)`,
      },
    }))

    return { years, series }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
      formatter: function (params) {
        let content = `<strong>${params[0].axisValue}</strong><br/>`
        let total = 0
        params.forEach(param => {
          content += `${param.seriesName}: ${param.value.toLocaleString()}<br/>`
          total += param.value
        })
        content += `<strong>Total: ${total.toLocaleString()}</strong>`
        return content
      },
    },
    legend: {
      bottom: 10,
      itemGap: 15,
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    grid: {
      left: 60,
      right: 30,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartData?.years || [],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: {
        color: '#6b7280',
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: {
        color: '#6b7280',
        formatter: '{value}',
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false, lineStyle: { color: '#f3f4f6' } },
    },
    series: chartData?.series || [],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No retention data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Geographic Enrollment Heatmap
function GeographicEnrollmentHeatmap({ data }) {
  const location = useLocation()

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Determine the appropriate location field to use based on current selection
    let locationField = 'subcounty' // default
    let locationLabel = 'Subcounty'

    if (location.showAllDistricts) {
      // If viewing all districts, group by district
      locationField = 'district'
      locationLabel = 'District'
    } else if (location.selectedVillage) {
      // If village selected, can't go deeper - show parish level
      locationField = 'parish'
      locationLabel = 'Parish'
    } else if (location.selectedParish) {
      // If parish selected, show villages within that parish
      locationField = 'village'
      locationLabel = 'Village'
    } else if (location.selectedSubcounty) {
      // If subcounty selected, show parishes within that subcounty
      locationField = 'parish'
      locationLabel = 'Parish'
    } else if (location.selectedDistrict) {
      // If district selected, show subcounties within that district
      locationField = 'subcounty'
      locationLabel = 'Subcounty'
    }

    // Group by the determined location field and year
    const locationData = {}

    data.forEach(record => {
      const locationName = record[locationField]
      const year = record.year
      const total = parseFloat(record.total) || 0

      if (!locationName) return // Skip records without the location field

      if (!locationData[locationName]) {
        locationData[locationName] = {}
      }
      if (!locationData[locationName][year]) {
        locationData[locationName][year] = 0
      }
      locationData[locationName][year] += total
    })

    // Convert to heatmap format
    const locations = Object.keys(locationData).sort()
    const years = [...new Set(data.map(d => d.year))].sort()

    if (locations.length === 0 || years.length === 0) {
      return null
    }

    const heatmapData = []
    locations.forEach((locationName, locIndex) => {
      years.forEach((year, yearIndex) => {
        const value = locationData[locationName][year] || 0
        heatmapData.push([yearIndex, locIndex, value])
      })
    })

    // Calculate min/max for color scaling
    const values = heatmapData.map(item => item[2])
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)

    return {
      locations,
      years,
      heatmapData,
      minValue,
      maxValue,
      locationLabel,
    }
  }, [
    data,
    location.showAllDistricts,
    location.selectedDistrict,
    location.selectedSubcounty,
    location.selectedParish,
    location.selectedVillage,
  ])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      position: 'top',
      formatter: function (params) {
        const [yearIndex, locIndex, value] = params.data
        const year = chartData.years[yearIndex]
        const location = chartData.locations[locIndex]
        return `${location}<br/>${year}: ${value.toLocaleString()} students`
      },
    },
    grid: {
      left: Math.max(120, chartData?.locationLabel === 'Village' ? 150 : 120),
      right: 30,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      splitArea: { show: true },
      axisLabel: { color: '#6b7280' },
    },
    yAxis: {
      type: 'category',
      data: chartData?.locations || [],
      splitArea: { show: true },
      axisLabel: {
        color: '#6b7280',
        fontSize: chartData?.locations?.length > 15 ? 8 : 10,
        formatter: function (value) {
          // Truncate long location names for better display
          return value.length > 20 ? value.substring(0, 17) + '...' : value
        },
      },
    },
    visualMap: {
      min: chartData?.minValue || 0,
      max: chartData?.maxValue || 1000,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 10,
      text: ['High', 'Low'],
      inRange: {
        color: ['#f0f9ff', '#0ea5e9', '#0369a1'],
      },
    },
    series: [
      {
        name: 'Enrollment',
        type: 'heatmap',
        data: chartData?.heatmapData || [],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No geographic data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Geographic Enrollment Heatmap Container with dynamic titles
function GeographicEnrollmentHeatmapContainer({ data }) {
  const location = useLocation()

  const { title, description } = useMemo(() => {
    let locationLevel = 'Subcounties'
    let contextDescription = 'across subcounties'

    if (location.showAllDistricts) {
      locationLevel = 'Districts'
      contextDescription = 'across all districts'
    } else if (location.selectedVillage) {
      locationLevel = 'Parishes'
      contextDescription = 'within parishes'
    } else if (location.selectedParish) {
      locationLevel = 'Villages'
      contextDescription = `within ${location.findLocationById(location.selectedParish)?.name || 'selected parish'} parish`
    } else if (location.selectedSubcounty) {
      locationLevel = 'Parishes'
      contextDescription = `within ${location.findLocationById(location.selectedSubcounty)?.name || 'selected subcounty'} subcounty`
    } else if (location.selectedDistrict) {
      locationLevel = 'Subcounties'
      contextDescription = `within ${location.findLocationById(location.selectedDistrict)?.name || 'selected district'} district`
    }

    return {
      title: `Geographic Enrollment Heatmap (${locationLevel})`,
      description: `Secondary school enrollment distribution ${contextDescription} over time (darker = higher enrollment)`,
    }
  }, [
    location.showAllDistricts,
    location.selectedDistrict,
    location.selectedSubcounty,
    location.selectedParish,
    location.selectedVillage,
    location.findLocationById,
  ])

  return (
    <ChartContainer title={title} description={description}>
      <GeographicEnrollmentHeatmap data={data} />
    </ChartContainer>
  )
}

// Completion Rate Trends (S6 Focus) Chart
function CompletionRateTrends({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Calculate completion rates (S6 enrollment vs S1 enrollment from 5 years prior)
    const yearData = {}

    data.forEach(record => {
      const year = record.year
      const s1 = parseFloat(record.s1) || 0
      const s6 = parseFloat(record.s6) || 0
      const total = parseFloat(record.total) || 0

      if (!yearData[year]) {
        yearData[year] = {
          s1Total: 0,
          s6Total: 0,
          totalEnrollment: 0,
          schools: new Set(),
        }
      }

      yearData[year].s1Total += s1
      yearData[year].s6Total += s6
      yearData[year].totalEnrollment += total
      yearData[year].schools.add(record.school_name)
    })

    const years = Object.keys(yearData).sort()

    // Calculate completion rates and S6 focus metrics
    const completionRates = []
    const s6EnrollmentData = []
    const s6PercentageData = []

    years.forEach(year => {
      const currentYear = yearData[year]
      // Look for S1 enrollment from 5 years ago for completion rate
      const baseYear = (parseInt(year) - 5).toString()
      const baseYearData = yearData[baseYear]

      // Completion rate (S6 now vs S1 from 5 years ago)
      let completionRate = 0
      if (baseYearData && baseYearData.s1Total > 0) {
        completionRate = (currentYear.s6Total / baseYearData.s1Total) * 100
      }

      // S6 as percentage of total enrollment
      const s6Percentage =
        currentYear.totalEnrollment > 0
          ? (currentYear.s6Total / currentYear.totalEnrollment) * 100
          : 0

      completionRates.push(completionRate)
      s6EnrollmentData.push(currentYear.s6Total)
      s6PercentageData.push(s6Percentage)
    })

    return {
      years,
      completionRates,
      s6EnrollmentData,
      s6PercentageData,
    }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      formatter: function (params) {
        let content = `<strong>${params[0].axisValue}</strong><br/>`
        params.forEach(param => {
          const value =
            param.seriesName.includes('Rate') ||
            param.seriesName.includes('Percentage')
              ? `${param.value.toFixed(1)}%`
              : param.value.toLocaleString()
          content += `${param.seriesName}: ${value}<br/>`
        })
        return content
      },
    },
    legend: {
      bottom: 10,
      itemGap: 20,
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    grid: {
      left: 60,
      right: 60,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: {
        color: '#6b7280',
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Students',
        position: 'left',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        splitLine: { show: false, lineStyle: { color: '#f3f4f6' } },
      },
      {
        type: 'value',
        name: 'Percentage (%)',
        position: 'right',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}%',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'S6 Total Enrollment',
        type: 'bar',
        yAxisIndex: 0,
        data: chartData?.s6EnrollmentData || [],
        itemStyle: { color: '#3b82f6' },
      },
      {
        name: 'Completion Rate',
        type: 'line',
        yAxisIndex: 1,
        data: chartData?.completionRates || [],
        itemStyle: { color: '#10b981' },
        lineStyle: { width: 3 },
        symbol: 'circle',
        symbolSize: 6,
      },
      {
        name: 'S6 % of Total',
        type: 'line',
        yAxisIndex: 1,
        data: chartData?.s6PercentageData || [],
        itemStyle: { color: '#f59e0b' },
        lineStyle: { width: 2, type: 'dashed' },
        symbol: 'diamond',
        symbolSize: 5,
      },
    ],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No completion data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Ownership Impact Analysis Chart
function OwnershipImpactAnalysis({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Group by ownership and year
    const ownershipData = {}

    data.forEach(record => {
      const ownership = record.ownership || 'Unknown'
      const year = record.year
      const total = parseFloat(record.total) || 0

      if (!ownershipData[ownership]) {
        ownershipData[ownership] = {}
      }
      if (!ownershipData[ownership][year]) {
        ownershipData[ownership][year] = { enrollment: 0, schools: new Set() }
      }
      ownershipData[ownership][year].enrollment += total
      ownershipData[ownership][year].schools.add(record.school_name)
    })

    const years = [...new Set(data.map(d => d.year))].sort()
    const ownerships = Object.keys(ownershipData)

    // Create series for total enrollment by ownership
    const enrollmentSeries = ownerships.map(ownership => ({
      name: `${ownership} (Total)`,
      type: 'line',
      yAxisIndex: 0,
      data: years.map(year => ownershipData[ownership][year]?.enrollment || 0),
      itemStyle: { color: ownership === 'Government' ? '#10b981' : '#3b82f6' },
      lineStyle: { width: 3 },
    }))

    // Create series for average enrollment per school
    const avgSeries = ownerships.map(ownership => ({
      name: `${ownership} (Avg per School)`,
      type: 'bar',
      yAxisIndex: 1,
      data: years.map(year => {
        const yearData = ownershipData[ownership][year]
        if (yearData && yearData.schools.size > 0) {
          return yearData.enrollment / yearData.schools.size
        }
        return 0
      }),
      itemStyle: {
        color:
          ownership === 'Government'
            ? 'rgba(16, 185, 129, 0.6)'
            : 'rgba(59, 130, 246, 0.6)',
      },
    }))

    return {
      years,
      series: [...enrollmentSeries, ...avgSeries],
    }
  }, [data])

  const option = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    legend: {
      bottom: 10,
      itemGap: 20,
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    grid: {
      left: 60,
      right: 60,
      top: 30,
      bottom: 60,
    },
    xAxis: {
      type: 'category',
      data: chartData?.years || [],
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#6b7280' },
      splitLine: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Total Enrollment',
        position: 'left',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        splitLine: { show: false, lineStyle: { color: '#f3f4f6' } },
      },
      {
        type: 'value',
        name: 'Avg per School',
        position: 'right',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: {
          color: '#6b7280',
          formatter: '{value}',
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        splitLine: { show: false },
      },
    ],
    series: chartData?.series || [],
  }

  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No ownership data available</p>
      </div>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      theme="ug-dashboard"
    />
  )
}

// Main Secondary School Trends Component
SecondarySchoolTrends.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      school_name: PropTypes.string,
      district: PropTypes.string,
      ownership: PropTypes.string,
      enrollment_male: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      enrollment_female: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      length: PropTypes.number,
      forEach: PropTypes.func,
      map: PropTypes.func,
      reduce: PropTypes.func,
    })
  ),
}

function SecondarySchoolTrends({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          No secondary school enrollment data available
        </p>
      </div>
    )
  }

  // Calculate summary stats
  const totalRecords = data.length
  const uniqueSchools = new Set(data.map(d => d.school_name)).size
  const uniqueYears = new Set(data.map(d => d.year)).size
  const totalStudents = data.reduce(
    (sum, record) => sum + (parseFloat(record.total) || 0),
    0
  )

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Secondary School Enrollment Trends
        </h2>
        <p className="text-gray-600 mb-4">
          Analysis of secondary school (S1-S6) enrollment data showing trends
          over time
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {totalRecords.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Records</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {uniqueSchools}
            </div>
            <div className="text-sm text-gray-600">Schools</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {uniqueYears}
            </div>
            <div className="text-sm text-gray-600">Years</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">
              {totalStudents.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Students</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="space-y-6">
        {/* High Priority Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartContainer
            title="Secondary School Enrollment Trends Over Time"
            description="Year-over-year secondary school enrollment showing boys vs girls patterns across S1-S6 grades"
          >
            <SecondarySchoolEnrollmentTrends data={data} />
          </ChartContainer>

          <ChartContainer
            title="Secondary School Gender Parity Evolution"
            description="Boys and girls secondary school enrollment with parity ratio tracking (100% = perfect parity)"
          >
            <SecondarySchoolGenderParity data={data} />
          </ChartContainer>
        </div>

        {/* School Performance Trajectory - Full Width */}
        <ChartContainer
          title="Secondary School Performance Trajectory"
          description="Individual school enrollment trends showing which secondary schools are growing vs declining (top 12 by growth rate)"
          height={'h-[530px]'}
        >
          <SchoolPerformanceTrajectory data={data} />
        </ChartContainer>

        {/* Medium Priority Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartContainer
            title="Grade-Level Retention Analysis (S1-S6)"
            description="Secondary school enrollment by grade level showing student progression and potential dropout points"
          >
            <GradeLevelRetentionAnalysis data={data} />
          </ChartContainer>

          <GeographicEnrollmentHeatmapContainer data={data} />
        </div>

        {/* Completion Focus */}
        <ChartContainer
          title="Secondary School Completion Rate Trends (S6 Focus)"
          description="S6 completion rates vs S1 enrollment from 5 years prior, showing secondary school progression and completion patterns"
        >
          <CompletionRateTrends data={data} />
        </ChartContainer>

        {/* Advanced Analysis */}
        <ChartContainer
          title="Secondary School Ownership Impact Analysis"
          description="Government vs Private secondary school enrollment trends and average enrollment per school"
        >
          <OwnershipImpactAnalysis data={data} />
        </ChartContainer>
      </div>
    </div>
  )
}

export default SecondarySchoolTrends
